import { useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { fetchConfirmedBuildMeta, isOutdatedBuild } from './build-meta'
import { dismissNewVersion, getDismissedBuildId, reloadToLatestVersion } from './refresh-cache'
import { isVersionCheckEnabled } from './version-check-gate'

const REFETCH_INTERVAL_MS = 5 * 60_000

const BOOTED_KEY = 'scope:version-check-booted'

export const NEW_VERSION_TOAST_ID = 'new-version'

export function VersionCheck() {
  const { t } = useTranslation()

  const { data: latest } = useQuery({
    queryKey: ['build-meta'],
    queryFn: fetchConfirmedBuildMeta,
    enabled: isVersionCheckEnabled(),
    staleTime: REFETCH_INTERVAL_MS,
    refetchInterval: REFETCH_INTERVAL_MS,
    refetchOnWindowFocus: true,
    retry: 1
  })

  const isOutdated = isOutdatedBuild(latest)

  useEffect(() => {
    if (!isOutdated || !latest) return

    const isFirstCheck = !sessionStorage.getItem(BOOTED_KEY)
    sessionStorage.setItem(BOOTED_KEY, '1')

    if (isFirstCheck) {
      reloadToLatestVersion()
      return
    }

    if (getDismissedBuildId() === latest.buildId) return

    toast(t('versionCheck.newVersionTitle'), {
      id: NEW_VERSION_TOAST_ID,
      description: t('versionCheck.newVersionDescription'),
      duration: Infinity,
      action: {
        label: t('versionCheck.update'),
        onClick: () => reloadToLatestVersion()
      },
      onDismiss: () => dismissNewVersion(latest.buildId),
      onAutoClose: () => dismissNewVersion(latest.buildId)
    })
  }, [isOutdated, latest, t])

  return null
}
